import { initialSelectionState } from '../core/components/annotations/providers/AnnotationsDataInitialState.js';
import type { PointsDisplay } from '../core/components/canvas/types/canvas-shape.js';
import { defaultRatio, SCALE_FALLBACK_COLOR } from '../core/constants.js';
import {
  GAP_POLICY_GAP,
  type TimeGapPolicy,
} from '../core/types/gap-policy.js';
import { ChartInteractionsConfig } from '../core/types/interactions.js';
import { ValueRepresentation } from '../core/types/scales.js';
import { SeriesCurve } from '../core/types/timeseries.js';
import { ToolbarPlacement } from '../core/types/toolbar.js';
import { TooltipVariant } from '../core/types/tooltip.js';
import type { TimeseriesChartState } from './types/state.js';
import {
  TimeseriesChartConfigProperties,
  TimeseriesChartLegendConfig,
  TimeseriesChartThresholdsConfig,
  TimeseriesChartYAxisConfig,
} from './types/timeseries-config.js';
import { TimeseriesToolbarInternalProps } from './types/toolbar.js';

export const DISPLAY_NAME = 'TimeseriesChart';

export const defaultTooltipVariant: TooltipVariant = 'single';
export const defaultSeriesDisplayMode = 'multi-line';

export const defaultYAxis: TimeseriesChartYAxisConfig = {};

export const defaultGapPolicy: TimeGapPolicy = GAP_POLICY_GAP;
export const defaultSeriesCurve: SeriesCurve = 'linear';
export const defaultPointsDisplay: PointsDisplay = 'auto';
export const defaultValueRepresentation: ValueRepresentation = 'absolute';
export const defaultInfiniteZoom = false;

export const DEFAULT_SCALE_FALLBACK_COLOR = SCALE_FALLBACK_COLOR;

export const defaultLegend: TimeseriesChartLegendConfig = {
  hidden: false,
  position: 'auto',
  ratio: defaultRatio,
};

export const defaultToolbar: TimeseriesToolbarInternalProps = {
  placement: 'top-right' as ToolbarPlacement,
  enabled: true,
};

export const defaultInteractions: ChartInteractionsConfig = {
  onZoomChange: undefined,
  pan: {
    enabled: true,
  },
  zoom: {
    enabled: true,
  },
};

export const defaultColorPalette = 'categorical';

export const defaultTimeseriesChartConfig: TimeseriesChartConfigProperties = {
  legend: defaultLegend,
  colorPalette: defaultColorPalette,
  gapPolicy: defaultGapPolicy,
  pointsDisplay: defaultPointsDisplay,
  valueRepresentation: defaultValueRepresentation,
  tooltip: {
    variant: defaultTooltipVariant,
    seriesDisplayMode: defaultSeriesDisplayMode,
  },
  toolbar: defaultToolbar,
  interactions: defaultInteractions,
};

export const defaultTimeseriesPerVariant = {
  line: [],
  area: [],
  bar: [],
  band: [],
  shapePriority: [],
};

export const defaultThreshold: TimeseriesChartThresholdsConfig = {
  left: [],
  right: [],
};

export const TIMESERIES_CHART_DEFAULT_VARIANT = 'line';

export const zIndicesTimeseriesAreas = {
  thresholdRange: 0,
  plot: 1,
  thresholdLine: 2,
  overlays: 3,
};

export const initialState: TimeseriesChartState = {
  filteredSeriesIds: new Set(),
  highlightedSeriesId: undefined,
  annotations: initialSelectionState,
  zoom: {
    zoomInEnabled: true,
    zoomOutEnabled: false,
    panEnabled: false,
  },
  currentDomain: undefined,
  select: undefined,
  interactionMode: 'explore',
  explore: {
    visible: false,
    pinned: false,
    position: undefined,
  },
  thresholds: { hoveredThresholdId: undefined },
};

export const TIMESERIES_ARIA_LABEL = 'Timeseries Chart';

export const TIMESERIES_TIME_AXIS_ARIA_LABEL = 'timeseries-time-axis';
export const AREA_CHART_DISPLAY_ARIA_LABEL = 'area-chart-display';
export const LINE_CHART_DISPLAY_ARIA_LABEL = 'line-chart-display';
export const BAR_CHART_DISPLAY_ARIA_LABEL = 'bar-chart-display';
